// 端数の丸めで行の最後のチップが次の行へ落ちないよう、1px を残してブラウザーに配らせる
const ROUNDING_ROOM = 1
const MAX_CANDIDATES = 20000

const total = row => row.reduce((sum, chipWidth) => sum + chipWidth, 0)
const slack = (row, rowWidth, gap) => rowWidth - total(row) - gap * (row.length - 1)
const fits = (row, rowWidth, gap) => slack(row, rowWidth, gap) >= 0

function greedyCounts(widths, rowWidth, gap) {
  const counts = []
  let row = []
  widths.forEach(chipWidth => {
    if (row.length > 0 && !fits([...row, chipWidth], rowWidth, gap)) {
      counts.push(row.length)
      row = []
    }
    row.push(chipWidth)
  })
  counts.push(row.length)
  return counts
}

function* compositions(count, rowCount) {
  if (rowCount === 1) {
    yield [count]
    return
  }
  for (let first = 1; first <= count - rowCount + 1; first += 1) {
    for (const rest of compositions(count - first, rowCount - 1)) yield [first, ...rest]
  }
}

function binomial(n, k) {
  let result = 1
  for (let i = 1; i <= k; i += 1) result = result * (n - k + i) / i
  return result
}

function slicesOf(widths, counts) {
  let start = 0
  return counts.map(count => widths.slice(start, (start += count)))
}

// 行ごとの「1 件あたりの余り」の最大と最小の差。小さい区切りほどどの行も余白がそろって見え、
// 余りの大きい 1 件だけの行も選ばれない
function spread(rows, rowWidth, gap) {
  const extras = rows.map(row => slack(row, rowWidth, gap) / row.length)
  return Math.max(...extras) - Math.min(...extras)
}

function splitCounts(widths, rowWidth, gap) {
  const greedy = greedyCounts(widths, rowWidth, gap)
  const candidateCount = binomial(widths.length - 1, greedy.length - 1)
  if (candidateCount > MAX_CANDIDATES) return greedy

  // 行より広いチップがあるとどの区切りも収まらないので、そのときは左から詰めた区切り (広いチップは 1 件で 1 行) のまま返す
  let best = greedy
  let bestSpread = spread(slicesOf(widths, greedy), rowWidth, gap)
  for (const counts of compositions(widths.length, greedy.length)) {
    const rows = slicesOf(widths, counts)
    if (!rows.every(row => fits(row, rowWidth, gap))) continue

    const candidateSpread = spread(rows, rowWidth, gap)
    if (candidateSpread < bestSpread) {
      best = counts
      bestSpread = candidateSpread
    }
  }
  return best
}

// 並びを保ったまま最少の行数に区切り、行ごとに件数と、行を埋めるために各チップへ足す幅を返す
export default function balancedRows(widths, rowWidth, gap) {
  if (widths.length === 0) return []

  return slicesOf(widths, splitCounts(widths, rowWidth, gap)).map(row => ({
    count: row.length,
    extra: Math.max(0, slack(row, rowWidth, gap) - ROUNDING_ROOM) / row.length
  }))
}
